<template>
    <div
      class="search-panel relative z-(--z-nav) flex items-center xl:w-full"
      :class="searchActive
        ? `max-xl:fixed max-xl:top-0 max-xl:bg-popover-solid max-xl:px-3
          max-sm:inset-x-0 max-sm:h-(--mobile-header-height)
          sm:max-xl:left-(--left-rail-width) sm:max-xl:right-0 sm:max-xl:h-12`
        : 'sm:max-xl:mr-5'"
    >
      <TasksFilters :key="project.id" :show="openFilterDlg" @close="openFilterDlg=false" />
      <div
        class="field-shell box-border h-8 cursor-pointer gap-2 px-2.5 hover:border-accent
          [-webkit-app-region:no-drag] xl:w-full"
        :class="searchActive
          ? 'max-xl:flex-1'
          : 'sm:max-xl:w-60 max-sm:size-10 max-sm:justify-center max-sm:rounded-full max-sm:border-0 max-sm:bg-transparent max-sm:p-0'"
        @click='showSearch()'
      >
        <IconMagnifyingGlass
          class="size-4 shrink-0 text-text-muted"
          :class="!searchActive && 'max-sm:size-5 max-sm:text-text'"
        />
        <input
          @keydown.up="activeItemIndex =
            (resultsList.length + activeItemIndex - 1) % resultsList.length"
          @keydown.down="activeItemIndex = (activeItemIndex + 1) % resultsList.length"
          @keydown.enter="goActiveItem"
          ref='searchInp'
          v-model="searchString"
          class="field-input w-full cursor-pointer"
          :class="!searchActive && 'max-sm:hidden'"
          :placeholder="t('search.search_or_navigate')"
          :disabled="disabled"
          title="Ctrl+G"
        />
        <i
          v-if="searchString"
          class="tr tr-close flex shrink-0 p-0.5 text-[0.75rem] text-text-muted max-sm:hidden"
          :title="$t('search.clear_all')"
          @click.stop="clearSearch"
        ></i>
      </div>
      <div v-if="searchActive" class="fixed inset-x-0 top-(--mobile-header-height) bottom-0 -z-1 bg-backdrop
             sm:top-12 sm:left-(--left-rail-width) xl:hidden" @click="closeSearchPopup"></div>
      <div
        :class="searchActive && 'popover-panel absolute inset-x-0 top-full mt-1.5 overflow-hidden max-xl:inset-x-3'"
        @mousedown.prevent
      >
        <div v-html="$t('search.hint')" :class="HELP_TEXT" v-if="searchActive && !showResults">
        </div>
        <div :class="HELP_TEXT" v-if="searchActive && showResults && !resultsList.length">
          {{ $t('search.nothing_found') }}
        </div>

        <div class="flex w-full flex-col" v-show="searchActive && showResults">
          <template  v-for="(r, i) in resultsList">
            <router-link
              v-if='r.routerLink'
              :key="`search-item-${i}-link`"
              :class="[RESULT_ITEM, activeItemIndex === i && 'bg-hover']"
              @mouseenter="activeItemIndex = i"
              :to="{ name: r.routerLink, params: r.routerLinkParams }">
                <SearchResultItem :item="r" />
            </router-link>
            <div v-else
                :key="`search-item-${i}-div`"
                :class="[RESULT_ITEM, activeItemIndex === i && 'bg-hover']"
                @mouseenter="activeItemIndex = i"
                @click="handleClick(r)">
              <SearchResultItem :item="r" />
            </div>
          </template>
        </div>
      </div>
    </div>

</template>
<script setup>
import { ref, computed, onMounted, watch, nextTick, onUnmounted  } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  mapActions,
  mapState,
  mapMutations,
  mapGetters,
} from 'vuex';
import SearchResultItem from '@/components/widgets/SearchResultItem';
import { IconMagnifyingGlass } from '@iconify-prerendered/vue-heroicons';
// import SimpleButton from '@/components/elements/SimpleButton';
import TasksFilters from '@/components/widgets/TasksFilters';
import { useTaskFilterChips } from '@/composables/useTaskFilterChips';

import eventBus from '@/eventBus';
import { useI18n } from 'vue-i18n';

const  { t } = useI18n();

const HELP_TEXT = 'px-5 py-6 text-center text-body leading-[1.4] text-muted';
const RESULT_ITEM = 'search-result-item flex h-12 w-full cursor-pointer items-center px-3 text-inherit';

const SYSTEM_ITEMS = [
  {
    label: 'search.open',
    text: 'menu.project_settings',
    icon: 'gear',
    inProject: true,
    routerLink: 'projectSettings',
  },
  {
    label: 'search.go_to',
    text: 'menu.summary_report',
    icon: 'summary-report',
    routerLink: 'workspace-summary',
  },
  {
    label: 'search.go_to',
    text: 'menu.time_reports',
    icon: 'time-spent-report',
    routerLink: 'tasks-time-spent',
  },
];

function doHighlitedSearch(where, what) {
  const whatIn = what.toLowerCase();
  const whereIn = where.toLowerCase();
  const foundIndex = whereIn.indexOf(whatIn);
  const found = foundIndex !== -1;
  let textBefore;
  let textSelected;
  let textAfter;
  if (found) {
    textBefore = where.slice(0, foundIndex);
    textSelected = where.slice(foundIndex, foundIndex + what.length);
    textAfter = where.slice(foundIndex + what.length, where.length);
  }
  return {
    found,
    textBefore,
    textSelected,
    textAfter,
  };
}

const props = defineProps({
  disabled: {
    type: Boolean,
    default: true,
  },
});


const searchActive = ref(false);
const searchString = ref('');
const activeItemIndex = ref(0);
const openFilterDlg = ref(false);

const store = useStore();
const router = useRouter();
const route = useRoute();
const { activeCount, clearAll } = useTaskFilterChips();

const searchInp = ref(null);

const showResults = computed(() => {
  return searchString.value.length;
});

const actionFilterTasks = (...args) => store.dispatch('project/actionFilterTasks', ...args);
const getUser = () => store.getters['user/getUser'];
const projectsList = computed(() => store.state.project.projectsList || []);


const project = computed(() => store.state.project.project);

const resultsList = computed(() => {
  if (!searchActive.value || !showResults.value) {
    return [];
  }
  const s = searchString.value;
  const foundSystemItems = [];
  SYSTEM_ITEMS.forEach((i) => {
    const {
      found,
      textBefore,
      textSelected,
      textAfter,
    } = doHighlitedSearch(t(i.text), s);
    if (found) {
      foundSystemItems.push({
        ...i,
        textBefore,
        textSelected,
        textAfter,
        ...(i.routerLink === 'projectSettings' ? { 
          routerLinkParams: {
            projectSlug: project.value.slug || 'last',
            slugNameProject: project.value.nameSlug || 'Last',
          } 
        }: {}),
      });
    }
  });
  projectsList.value.some((p) => {
    if (!p?.name) {
      return false;
    }
    const {
      found,
      textBefore,
      textSelected,
      textAfter,
    } = doHighlitedSearch(p.name, s);
    if (found) {
      foundSystemItems.push({
        label: 'search.go_to',
        project: p,
        routerLink: 'project',
        routerLinkParams: { 
          projectSlug: p.slug,
          slugNameProject: p.nameSlug,
        },
        textBefore,
        textSelected,
        textAfter,
      });

      return true;
    }
    return false;
  });
  const searchInTasks = [
    {
      label: 'search.in_tasks',
      textSelected: s,
      inProject: true,
      onclick: () => {
        runSearch(s);
      },
    },
  ];
  // this project might be null if we are e.g. on report page
  ((project.value && project.value.projectMembers) || []).some((pm) => {
    const user = getUser()(pm.userId);

    if (!user || !user.username) {
      return false;
    }
    const {
      found,
      textBefore,
      textSelected,
      textAfter,
    } = doHighlitedSearch(user.username, s);
    if (found) {
      foundSystemItems.push({
        label: t('search.tasks_on'),
        user,
        textBefore,
        textSelected,
        textAfter,
        inProject: true,
        onclick: () => {
          applyFilter({ user });
        },
      });
      return true;
    }
    return false;
  });
  (() => {
    const {
      found,
      textBefore,
      textSelected,
      textAfter,
    } = doHighlitedSearch(t('search.unassigned'), s);
    if (found) {
      foundSystemItems.push({
        label: t('search.tasks_on'),
        user: { id: null },
        textBefore,
        textSelected,
        textAfter,
        inProject: true,
        onclick: () => {
          applyFilter({ user: { id: null } });
        },
      });
    }
  })();

  ((project.value && project.value.labels) || []).some((tag) => {
    const {
      found,
      textBefore,
      textSelected,
      textAfter,
    } = doHighlitedSearch(tag.name, s);
    if (found) {
      foundSystemItems.push({
        label: 'ui.searcher_with_tag', // i18n key, translated in SearchResultItem
        tag,
        textBefore,
        textSelected,
        textAfter,
        inProject: true,
        onclick: () => {
          applyFilter({ tag });
        },
      });
      return true;
    }
    return false;
  });
  (() => {
    const {
      found,
      textBefore,
      textSelected,
      textAfter,
    } = doHighlitedSearch(t('ui.searcher_no_tags'), s);
    if (found) {
      foundSystemItems.push({
        label: t('search.noTag'),
        tag: null,
        textBefore,
        textSelected,
        textAfter,
        inProject: true,
        onclick: () => {
          applyFilter({ tag: { id: null, name: t('search.noTag') } });
        },
      });
      return true;
    }
    return false;
  })();
  const result = [
    ...foundSystemItems,
    ...searchInTasks,
  ];

  console.log('result 🚩🚩🚩🚩', JSON.stringify(result, null, 2));
  if (route.name !== 'project') {
    return result.filter(i => !i.inProject);
  }
  return result;
});

const projectTasksFilter = computed(() => store.state.project.projectTasksFilter);

const globalKeyListener = (e) => {
  if ((e.key === 'g' || e.keyCode === 71) && (e.ctrlKey || e.metaKey)) {
    e.preventDefault(); // present "Save Page" from getting triggered.
    showSearch();
  }
};
const globalClickListener = (e) => {
  // self-writen click outside
  const insideSearch = e.target.closest('.search-panel') || e.target.closest('.search-slot');
  if ((!insideSearch || e.target.closest('.search-result-item'))
    && !e.target.closest('.search-badge')
    && !e.target.closest('.filters-panel')
  ) {
    closeSearchPopup();
  }
};

function showSearch() {
  searchActive.value = true;
  nextTick(() => {
    searchInp.value.focus();
  });
}

function runSearch() {
  actionFilterTasks({
    projectId: project.value.id,
    ...projectTasksFilter.value,
    searchString: searchString.value,
  });
  searchString.value = '';
}

function applyFilter({ user, tag }) {
  searchString.value = '';
  actionFilterTasks({
    projectId: project.value.id,
    assigneeIds: user ? [user.id] : undefined,
    tagIds: tag ? [tag.id] : undefined,
  });
}

function clearSearch() {
  searchString.value = '';
  if (activeCount.value) {
    clearAll();
  }
}

function handleClick(r) {
  r.onclick();
  closeSearchPopup();
}

function closeSearchPopup() {
  searchActive.value = false;
  if (searchInp) {
    searchInp.value.blur();
  }
}

function goActiveItem() {
  const resList = resultsList.value;
  if (resList.length && searchString.value.length) {
    const res = resList[activeItemIndex.value];
    if (res.routerLink) {
      router.push({ name: res.routerLink, params: res.routerLinkParams });
      closeSearchPopup();
      searchString.value = '';
    } else {
      handleClick(res);
    }
  }
}

watch(
  resultsList,
  (val) => {
    if (val.length !== 0 && activeItemIndex.value > val.length - 1) {
      activeItemIndex.value = val.length - 1;
    }
  }
);

watch(() => route.fullPath, () => {
  openFilterDlg.value = false;
});

const toggleFilterDlg = () => {
  openFilterDlg.value = !openFilterDlg.value;
};

watch(openFilterDlg, (opened) => {
  if (opened) eventBus.$emit('tasks-filters-opened');
});
const closeFilterDlg = () => {
  openFilterDlg.value = false;
};

onMounted(() => {
  document.addEventListener('keydown', globalKeyListener);
  document.addEventListener('click', globalClickListener, true);
  eventBus.$on('toggle-tasks-filters', toggleFilterDlg);
  eventBus.$on('close-tasks-filters', closeFilterDlg);

  eventBus.$off('memberSelect');
  eventBus.$on('memberSelect', (user) => {
    console.log('memberSelect on', user);
    if ((projectTasksFilter.value.assigneeIds || []).includes(user.id)) {
      clearSearch();
    } else {
      applyFilter({ user });
    }
  });
  // component will be remounted when we go e.g. from project to report
  if (router.currentRoute.name === 'tasks-time-spent'
    || router.currentRoute.name === 'workspace-summary') {
    clearSearch();
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', globalKeyListener);
  document.removeEventListener('click', globalClickListener, true);
  eventBus.$off('toggle-tasks-filters', toggleFilterDlg);
  eventBus.$off('close-tasks-filters', closeFilterDlg);
});
</script>
